import { HeadingLevel, IParagraphOptions, Paragraph, TextRun } from 'docx'
import isEmpty from 'lodash/isEmpty'
import { DraftSectionItem } from '../../components/Section/Section.model'
import {
  DraftQuestionnaireEntry,
  EntryType,
  QuestionTypeCode
} from '../../data/model/questionnaire'
import { MarketOption } from '../../modules/Questionnaire/MarketVisibility/useProjectMarkets'
import { ResponseOptionsByQuestion } from '../../modules/Questionnaire/Questionnaire.slice'
import { flattenEntries } from '../../modules/Questionnaire/Questionnaire.utils'
import { withMarketAudienceEntries } from '../../modules/Questionnaire/audienceDisplayLogic'
import {
  MarketAudience,
  emptyMarketAudience
} from '../../modules/Questionnaire/marketAudienceContext'
import { asyncFlatMap } from '../asyncMaps'
import { getQuestionType } from '../questionnaireUtils'
import { createEntryContentBasic } from './createEntryContentBasic'
import { createEntryContentConjoint } from './createEntryContentConjoint'
import { createEntryContentFork } from './createEntryContentFork'
import { createEntryContentFreeText } from './createEntryContentFreeText'
import { createEntryContentMatrix } from './createEntryContentMatrix'
import { createEntryContentMaxDiff } from './createEntryContentMaxDiff'
import { createEntryContentRanked } from './createEntryContentRanked'
import { createEntryContentSlider } from './createEntryContentSlider'
import { createEntryContentTextCard } from './createEntryContentTextCard'
import { createEntryContentTextHighlighter } from './createEntryContentTextHighlighter'
import { createLogicHeader } from './createLogicHeader'
import { createMultilinedTextRuns } from './createMultilinedTextRuns'
import { createQuestionTitle } from './createQuestionTitle'
import { isEntryHiddenInMarket } from './marketFilter'
import { paragraphTopBorderOptions } from './paragraphTopBorderOptions'

const getQuestionText = ({ entryType, entryItem }: DraftQuestionnaireEntry) => {
  switch (entryType) {
    case EntryType.TextCardEntryType:
      return entryItem.textCard.title
    case EntryType.MatrixEntryType:
      return entryItem.matrixTitle?.title ?? ''
    case EntryType.ForkEntryType:
      return entryItem.fork.name
    case EntryType.SectionEntryType:
      return entryItem.title
    default:
      return entryItem.question?.text ?? ''
  }
}

const createQuestionContent = async (
  entry: DraftQuestionnaireEntry,
  entries: DraftQuestionnaireEntry[],
  allMarkets: MarketOption[],
  marketSurveyId: string | undefined
): Promise<Paragraph[]> => {
  if (entry.entryType === EntryType.ForkEntryType) {
    return createEntryContentFork(entry)
  }

  if (entry.entryType === EntryType.SectionEntryType) {
    return [
      new Paragraph({
        children: [
          new TextRun({
            text: '',
            break: 1
          }),
          new TextRun({
            text: `S${entry.contextPosition + 1} `,
            bold: true
          }),
          new TextRun('(Section - end)')
        ]
      })
    ]
  }

  const questionType = getQuestionType(entry)

  switch (questionType) {
    case QuestionTypeCode.CustomAudience:
    case QuestionTypeCode.StandardAudience:
    case QuestionTypeCode.Basic:
      return createEntryContentBasic(entry, entries, allMarkets, marketSurveyId)
    case QuestionTypeCode.Scale:
      return createEntryContentSlider(entry)
    case QuestionTypeCode.Matrix:
      return createEntryContentMatrix(
        entry as DraftQuestionnaireEntry<EntryType.MatrixEntryType>,
        entries,
        allMarkets,
        marketSurveyId
      )
    case QuestionTypeCode.Ranked:
      return createEntryContentRanked(
        entry,
        entries,
        allMarkets,
        marketSurveyId
      )
    case QuestionTypeCode.TextCard:
      return createEntryContentTextCard(entry)
    case QuestionTypeCode.FreeText:
      return createEntryContentFreeText(entry)
    case QuestionTypeCode.TextHighlighter:
      return createEntryContentTextHighlighter(entry)
    case QuestionTypeCode.MaxDiff:
      return createEntryContentMaxDiff(entry, marketSurveyId)
    case QuestionTypeCode.Conjoint:
      return createEntryContentConjoint(entry)
    default:
      return []
  }
}

export const createQuestions = ({
  entries = [],
  audienceEntries = [],
  marketAudience = emptyMarketAudience,
  responseOptionsByQuestion,
  questionnaireEntries,
  allMarkets = [],
  marketSurveyId
}: {
  entries?: DraftQuestionnaireEntry[]
  audienceEntries?: DraftQuestionnaireEntry[]
  marketAudience?: MarketAudience
  responseOptionsByQuestion: ResponseOptionsByQuestion
  questionnaireEntries?: DraftQuestionnaireEntry[]
  allMarkets?: MarketOption[]
  // Per-market export: entries hidden in this market (and sections with no
  // visible child) are skipped, but numbering and every lookup (display logic,
  // masking, "skip to") still run against the full master list.
  marketSurveyId?: string
}): Promise<Paragraph[]> => {
  const allEntries = questionnaireEntries || [...audienceEntries, ...entries]
  const anyEntries = !isEmpty(entries) ? entries : audienceEntries
  const flattenedAllEntries = flattenEntries(allEntries)
  const logicLookupEntries = withMarketAudienceEntries(
    flattenedAllEntries,
    marketAudience
  )
  const contentLookupEntries = withMarketAudienceEntries(
    allEntries,
    marketAudience
  )

  return asyncFlatMap<DraftQuestionnaireEntry, Paragraph>(
    anyEntries,
    async (entry) => {
      if (isEntryHiddenInMarket(entry, marketSurveyId)) {
        return []
      }

      const logicHeader = createLogicHeader(
        entry,
        logicLookupEntries,
        responseOptionsByQuestion
      )
      const result: Paragraph[] = []

      if (logicHeader) {
        result.push(logicHeader)
      }

      if (entry.entryType !== EntryType.ForkEntryType) {
        result.push(
          createQuestionTitle({
            entry,
            shouldHaveTopBorder: !logicHeader,
            // No "Hidden in: …" annotations in a per-market doc.
            allMarkets: marketSurveyId ? [] : allMarkets
          })
        )
      }

      const questionTextParts = getQuestionText(entry)
      const questionTextParagraphOptions: IParagraphOptions = {
        heading: HeadingLevel.HEADING_2,
        // make empty lines if the question's text is multilined
        children: createMultilinedTextRuns(questionTextParts)
      }

      result.push(
        new Paragraph(
          entry.entryType === EntryType.ForkEntryType ||
          entry.entryType === EntryType.SectionEntryType
            ? { ...questionTextParagraphOptions, ...paragraphTopBorderOptions }
            : questionTextParagraphOptions
        )
      )

      if (entry.entryType === EntryType.SectionEntryType) {
        const entryItem = entry.entryItem as DraftSectionItem
        result.push(
          ...(await createQuestions({
            entries: entryItem.entries,
            audienceEntries,
            marketAudience,
            responseOptionsByQuestion,
            questionnaireEntries: flattenedAllEntries,
            allMarkets,
            marketSurveyId
          }))
        )
      }

      result.push(
        ...(await createQuestionContent(
          entry,
          contentLookupEntries,
          allMarkets,
          marketSurveyId
        ))
      )

      return result
    }
  )
}
